<template>
  <div class="d-flex flex-column min-vh-100">
    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-8 mt-8">
            <h1 class="text-primary weight-600">Import CMM Submission</h1>
            <p class="text-grey mt-2">Enter the CoverMyMeds submission details and upload supporting documents.</p>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <!-- autocomplete/name chosen to avoid the browser's one-time-code autofill dropping
                     an emailed MFA code into this field (LAM-4598). -->
                <v-text-field
                  v-model="submissionKey"
                  label="CMM Submission Key"
                  placeholder="e.g. BXNQ2K3P"
                  autocomplete="off"
                  name="cmm-submission-key"
                  :rules="[rules.required, rules.cmmKey]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="patientLastName"
                  label="Patient Last Name"
                  placeholder="Enter patient last name"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <TextDateInput
                  v-model="patientDobDate"
                  label="Patient Date of Birth"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-textarea
                  v-model="extraContextNotes"
                  label="Additional Context (optional)"
                  placeholder="e.g. medical history, vaccinations, payor requirements, requests for expedited review, etc."
                  rows="3"
                  maxlength="3500"
                  counter="3500"
                  class="mb-2 lh--force-white-background" />

                <div class="advanced mb-5">
                  <button
                    type="button"
                    class="advanced__toggle"
                    :aria-expanded="showAdvanced"
                    @click="showAdvanced = !showAdvanced">
                    <span class="advanced__heading">
                      <span class="text-subtitle-2 text-primary weight-600">Additional details</span>
                      <span class="text-caption text-medium-emphasis">
                        Optional — values CoverMyMeds may need that do not always import
                      </span>
                    </span>
                    <v-icon size="22" class="advanced__chevron" :class="{ 'advanced__chevron--open': showAdvanced }">
                      mdi-chevron-down
                    </v-icon>
                  </button>

                  <v-expand-transition>
                    <div v-show="showAdvanced" class="advanced__body">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-combobox
                            v-model="packageSizeUnit"
                            :items="packageSizeUnits"
                            label="Package size unit"
                            placeholder="e.g. Gram, Each, mL"
                            :rules="packageSizeUnits.length ? [(v) => valueInItemsRule(v, packageSizeUnits)] : []"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  CoverMyMeds requires a package size unit (dosage form) to submit. It is often not
                                  carried over from the pharmacy system or the prescription, so setting it here keeps
                                  the submission from stalling.
                                </span>
                              </v-tooltip>
                            </template>
                          </v-combobox>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="patientPhone"
                            type="tel"
                            label="Patient phone number"
                            placeholder="e.g. (555) 123-4567"
                            :rules="[usPhoneRule]"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  Plans frequently require a patient phone number. Adding it here avoids a follow-up
                                  when CoverMyMeds did not capture one.
                                </span>
                              </v-tooltip>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="daw"
                            :items="['Yes', 'No']"
                            label="DAW"
                            clearable
                            placeholder="Default (No)"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  Set to Yes when the prescriber requires the brand be dispensed as written. Leave blank
                                  to use the default (No).
                                </span>
                              </v-tooltip>
                            </template>
                          </v-select>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="prescriptionNumber"
                            label="Rx number"
                            placeholder="e.g. 1234567"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  The pharmacy's prescription (Rx) number, if available. Allows looking up this order by
                                  Rx number later.
                                </span>
                              </v-tooltip>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="expeditedReview"
                            :items="expeditedOptions"
                            label="Expedited review"
                            clearable
                            placeholder="Use default"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  Request an urgent (within 24h) review for this order. Leave blank to use your account
                                  default.
                                </span>
                              </v-tooltip>
                            </template>
                          </v-select>
                        </v-col>

                        <v-col v-if="doaAttestationEnabled" cols="12" sm="6">
                          <v-select
                            id="doa-attestation"
                            v-model="prescriberDoaAttested"
                            :items="doaOptions"
                            label="DOA with prescriber"
                            clearable
                            placeholder="Default (No)"
                            hide-details="auto"
                            class="lh--force-white-background">
                            <template #append-inner>
                              <v-tooltip location="top" max-width="320" open-delay="100">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="15" class="advanced__help">
                                    mdi-help-circle-outline
                                  </v-icon>
                                </template>
                                <span>
                                  Set to Yes if there is a delegation of authority (DOA) with this prescriber. Leave
                                  blank for No.
                                </span>
                              </v-tooltip>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>
                </div>

                <h3 class="text-primary weight-600 mb-2">Upload Documents</h3>
                <ChartPagePickerDialog
                  v-if="pickerProps"
                  :model-value="true"
                  :download-url="pickerProps.downloadUrl"
                  :per-page-bytes="pickerProps.perPageBytes"
                  :budget-bytes="pickerProps.budgetBytes"
                  :allow-over-budget="pickerProps.allowOverBudget"
                  @update:model-value="!$event && closePicker()"
                  @confirm="confirmPicker" />
                <div
                  v-for="(doc, index) in supplementalDocs"
                  :key="index"
                  class="doc-drop-zone mb-3 pa-3 rounded-lg d-flex align-center"
                  :class="{
                    'doc-drop-zone--active': dragOverIndex === index && !doc.links.length,
                    'doc-drop-zone--reject': dragOverIndex === index && doc.links.length,
                  }"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(index)"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="handleFileDrop(index, $event)">
                  <input
                    :ref="(el) => (fileInputRefs[index] = el)"
                    type="file"
                    accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleFileSelect(index, $event)" />
                  <v-icon class="mr-3 flex-shrink-0" :color="doc.links.length ? 'success' : 'grey'">
                    {{ doc.links.length ? "mdi-check-circle" : "mdi-file-outline" }}
                  </v-icon>
                  <div class="flex-grow-1" style="min-width: 0">
                    <v-text-field
                      v-model="doc.label"
                      placeholder="No file selected"
                      variant="plain"
                      density="compact"
                      hide-details
                      class="text-body-2" />
                    <ChartPrepareStatus
                      :preparing="doc.preparing"
                      :recompressing="doc.recompressing"
                      :error="doc.error"
                      :dropped-pages="doc.droppedPages"
                      :origin-bytes="doc.originBytes"
                      :size-bytes="doc.sizeBytes"
                      :compression-level="doc.level"
                      :can-choose-pages="canChoosePages(doc)"
                      @choose-pages="openPicker(index)" />
                  </div>
                  <v-btn
                    v-if="doc.links.length"
                    :href="doc.links[0]"
                    target="_blank"
                    icon="mdi-download"
                    variant="text"
                    size="small"
                    color="primary"
                    class="ml-1 flex-shrink-0" />
                  <v-btn
                    v-else
                    variant="tonal"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-upload"
                    class="ml-2 flex-shrink-0"
                    :loading="doc.uploading || doc.preparing"
                    @click="fileInputRefs[index]?.click()">
                    Upload
                  </v-btn>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    color="error"
                    class="ml-1 flex-shrink-0"
                    @click="removeDoc(index)" />
                </div>
                <p v-if="budgetCaption()" class="text-caption text-medium-emphasis mb-2">{{ budgetCaption() }}</p>
                <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" class="mb-4" @click="addDoc">
                  Add Supplemental Document
                </v-btn>

                <ProviderDefaultCommunicationMethod
                  ref="providerDefaultCommunicationMethodRef"
                  v-model:enabled="forwardResponsesToProviderByEmail"
                  v-model:email="providerEmail"
                  :show-errors="importFormSubmitted"
                  class="mb-4" />

                <v-btn
                  color="primary"
                  block
                  size="large"
                  type="submit"
                  :loading="isSubmitting"
                  :disabled="isSubmitting || !!importedOrderKey">
                  Import Submission
                </v-btn>

                <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>

                <v-alert v-if="statusMessage" type="info" variant="tonal" class="mt-4">
                  {{ statusMessage }}
                </v-alert>

                <div v-if="importedOrderKey && !isDuplicate" class="import-routing mt-4">
                  <IntakeTagBar
                    :tags="importedTags"
                    :tag-defs="paAvailableTags"
                    :tag-due-dates="importedTagDueDates"
                    entity-type="submission"
                    :entity-id="importedOrderKey"
                    @update:tags="(t) => (importedTags = t)"
                    @update:tag-due-dates="(d) => (importedTagDueDates = d)" />
                  <AssigneeBar
                    :assignees="importedAssignees"
                    entity-type="submission"
                    :entity-id="importedOrderKey"
                    @update:assignees="(a) => (importedAssignees = a)" />
                  <GroupBar
                    :assigned-groups="importedAssignedGroups"
                    entity-type="submission"
                    :entity-id="importedOrderKey"
                    @update:assigned-groups="(g) => (importedAssignedGroups = g)" />
                </div>

                <div v-if="importedOrderKey" class="d-flex ga-3 mt-4">
                  <v-btn color="primary" variant="outlined" @click="viewOrder">
                    {{ isDuplicate ? "View Existing Submission" : "View Order" }}
                  </v-btn>
                  <v-btn color="primary" variant="tonal" @click="resetForm">
                    {{ isDuplicate ? "Try a Different Key" : "Import Another" }}
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- No Documents Warning Dialog -->
    <v-dialog v-model="showNoDocsWarning" max-width="480" persistent>
      <v-card class="pa-2">
        <v-card-text class="text-center pt-6 pb-2">
          <v-icon size="48" color="warning" class="mb-4">mdi-file-alert-outline</v-icon>
          <h3 class="text-primary weight-600 mb-3">No Documents Attached</h3>
          <p class="text-body-1 text-medium-emphasis mb-2">
            This submission does not include any supporting documents. The order will not be processed until the
            required documentation has been provided.
          </p>
          <p class="text-body-2 text-medium-emphasis">
            Would you like to go back and upload documents, or proceed without them?
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-2">
          <v-spacer />
          <v-btn variant="outlined" color="primary" @click="showNoDocsWarning = false">Go Back</v-btn>
          <v-btn variant="flat" color="warning" :loading="isSubmitting" @click="confirmImportWithoutDocs">
            Import Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { orderInfoService } from "@/services/orderInfoService";
import { tryParseApiErrors } from "@/utils";
import { localDateToIso } from "@/utils/dateUtils";
import { valueInItemsRule, usPhoneRule } from "@/utils/form";
import { CMM_KEY_REGEX } from "@/utils/validators";
import TextDateInput from "@/components/TextDateInput.vue";
import ProviderDefaultCommunicationMethod from "@/components/ProviderDefaultCommunicationMethod.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useDemoCmmImport } from "@/demo/useDemoCmmImport";
import { OVER_BUDGET_MESSAGE, useChartUpload } from "@/composables/useChartUpload";
import ChartPagePickerDialog from "@/components/ChartPagePickerDialog.vue";
import ChartPrepareStatus from "@/components/ChartPrepareStatus.vue";

const route = useRoute();
const router = useRouter();
const requirementsStore = useRequirementsStore();
const formRef = ref(null);

const submissionKey = ref("");
const patientLastName = ref("");
const patientDobDate = ref(null);
const extraContextNotes = ref("");
const patientPhone = ref("");
const packageSizeUnit = ref(null);
const daw = ref(null);
const prescriptionNumber = ref("");
const expeditedReview = ref(null);
const showAdvanced = ref(true);

// Package size unit options are the canonical DRUG_QUANTITY_UNITS list, served via client config.
const packageSizeUnits = computed(() => requirementsStore.clientConfig?.intake?.packageSizeUnits || []);
const paAvailableTags = computed(() => requirementsStore.clientConfig?.priorAuth?.availableTags || []);
// Tri-state: Yes/No override, or blank to fall back to the client's default_expedited_review.
const expeditedOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const {
  docs: supplementalDocs,
  fileInputRefs,
  dragOverIndex,
  addDoc,
  removeDoc,
  resetDocs,
  hasUploadedDocs,
  isAnyUploadInProgress,
  isOverBudget,
  budgetCaption,
  canChoosePages,
  pickerProps,
  openPicker,
  closePicker,
  confirmPicker,
  onDragEnter,
  onDragLeave,
  handleFileDrop,
  handleFileSelect,
} = useChartUpload();
const isSubmitting = ref(false);
const errors = ref([]);
const statusMessage = ref(null);
const importedOrderKey = ref(null);
const importedAssignees = ref([]);
const importedAssignedGroups = ref([]);
const importedTags = ref([]);
const importedTagDueDates = ref([]);
const isDuplicate = ref(false);
const isReimport = ref(false);
const showNoDocsWarning = ref(false);
const forwardResponsesToProviderByEmail = ref(false);
// Smart Submit (LAM-5919): the DOA attestation can be given at import and changed on the review page.
const doaAttestationEnabled = computed(
  () =>
    requirementsStore.clientConfig?.priorAuth?.delegateAccountSubmitRequired === true &&
    requirementsStore.clientConfig?.priorAuth?.smartSubmitEnabled === true
);
// Yes/No like the other Additional details selects; blank means No.
const prescriberDoaAttested = ref(null);
const doaOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const providerEmail = ref(null);
const providerDefaultCommunicationMethodRef = ref(null);
const importFormSubmitted = ref(false);

onMounted(async () => {
  const prefillKey = route.query.key;
  // Don't clobber a key the user already typed before this component mounted.
  if (typeof prefillKey === "string" && prefillKey.trim() && !submissionKey.value) {
    submissionKey.value = prefillKey.trim().toUpperCase();
  }
  // Ensure package size unit options are available even if the sidebar hasn't loaded client config yet.
  // Non-fatal if it fails — the combobox still accepts free text and the backend validates.
  if (!requirementsStore.clientConfig) {
    try {
      await requirementsStore.refreshCurrentUserClientConfig();
    } catch (e) {
      // ignore — leave the dropdown empty rather than block the import form
    }
  }
});

const rules = {
  required: (v) => !!v || "This field is required",
  cmmKey: (v) => {
    if (!v) return true;
    const cleaned = v
      .replace(/^key\s*:\s*/i, "")
      .trim()
      .toUpperCase();
    return CMM_KEY_REGEX.test(cleaned) || "Enter a valid CMM key (6\u20138 letters/digits, e.g. BXNQ2K3P).";
  },
};

const patientDobIso = computed(() => localDateToIso(patientDobDate.value));

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  errors.value = [];
  importFormSubmitted.value = true;

  const { valid } = await formRef.value.validate();
  if (!valid) return;
  if (!providerDefaultCommunicationMethodRef.value?.validate()) return;

  if (isAnyUploadInProgress()) {
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }

  if (isOverBudget()) {
    errors.value = [OVER_BUDGET_MESSAGE];
    return;
  }

  if (!hasUploadedDocs()) {
    showNoDocsWarning.value = true;
    return;
  }

  await performSubmit();
};

const confirmImportWithoutDocs = async () => {
  if (isAnyUploadInProgress()) {
    showNoDocsWarning.value = false;
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }
  showNoDocsWarning.value = false;
  importFormSubmitted.value = true;
  if (!providerDefaultCommunicationMethodRef.value?.validate()) return;
  await performSubmit();
};

const performSubmit = async () => {
  submissionKey.value = submissionKey.value
    .replace(/^key\s*:\s*/i, "")
    .trim()
    .toUpperCase();
  const extraContext = extraContextNotes.value.trim();
  errors.value = [];
  statusMessage.value = null;
  isSubmitting.value = true;

  try {
    const uploadedDocs = supplementalDocs.value
      .filter((doc) => doc.links.length > 0)
      .map((doc) => ({ label: doc.label, links: doc.links, context: "" }));
    const payload = {
      submission_key: submissionKey.value,
      patient_last_name: patientLastName.value,
      patient_dob: patientDobIso.value,
      patient_chart: uploadedDocs,
    };
    if (extraContext) {
      payload.extra_structured_information = { additional_context: extraContext };
    }
    const phone = patientPhone.value?.trim();
    if (phone) payload.patient_phone = phone;
    const unit = typeof packageSizeUnit.value === "string" ? packageSizeUnit.value.trim() : packageSizeUnit.value;
    if (unit) payload.package_size_unit = unit;
    if (daw.value) payload.daw = daw.value;
    const rxNum = prescriptionNumber.value?.trim();
    if (rxNum) payload.prescription_number = rxNum;
    // Send only when explicitly set (Yes/No); blank leaves it to the client default. Guard on null,
    // not falsiness, so an explicit "No" (false) is preserved.
    if (expeditedReview.value !== null && expeditedReview.value !== undefined) {
      payload.expedited_review_within24h = expeditedReview.value;
    }
    payload.forward_responses_to_provider_by_email = forwardResponsesToProviderByEmail.value;
    if (doaAttestationEnabled.value) payload.prescriber_doa_attested = prescriberDoaAttested.value === true;
    if (forwardResponsesToProviderByEmail.value) {
      payload.provider_email = providerEmail.value?.trim();
    }
    const response = await orderInfoService.importCMMSubmission(payload);
    const orderKey = response?.orderKey;
    if (!orderKey) {
      errors.value = ["Submission was created but no order key was returned. Please check All Submissions."];
      return;
    }
    importedOrderKey.value = orderKey;
    importedAssignees.value = response?.assignees || [];
    importedAssignedGroups.value = response?.assignedGroups || [];
    importedTags.value = response?.statusTags || [];
    importedTagDueDates.value = response?.tagDueDates || [];
    isReimport.value = !!response?.wasReimport;
    statusMessage.value = isReimport.value
      ? "Patient information updated. Re-running the import with the corrected details."
      : "Import started. We're fetching this submission from CoverMyMeds.";
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
    // Error bodies skip camelization and DRF quotes their numbers.
    const oversizedDocuments = error?.response?.data?.documents;
    if (Array.isArray(oversizedDocuments)) {
      errors.value.push(
        ...oversizedDocuments.map((doc) => `${doc.label}: ${(Number(doc.size) / (1024 * 1024)).toFixed(1)} MB`)
      );
    }
    // 409 duplicate-key: offer a link to the pre-existing submission
    const existingKey = error?.response?.data?.existing_order_key;
    if (error?.response?.status === 409 && existingKey) {
      importedOrderKey.value = existingKey;
      isDuplicate.value = true;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  submissionKey.value = "";
  patientLastName.value = "";
  patientDobDate.value = null;
  extraContextNotes.value = "";
  patientPhone.value = "";
  packageSizeUnit.value = null;
  daw.value = null;
  prescriptionNumber.value = "";
  expeditedReview.value = null;
  showAdvanced.value = true;
  resetDocs();
  errors.value = [];
  statusMessage.value = null;
  importedOrderKey.value = null;
  importedAssignees.value = [];
  importedAssignedGroups.value = [];
  importedTags.value = [];
  importedTagDueDates.value = [];
  isDuplicate.value = false;
  isReimport.value = false;
  showNoDocsWarning.value = false;
  forwardResponsesToProviderByEmail.value = false;
  prescriberDoaAttested.value = null;
  providerEmail.value = null;
  importFormSubmitted.value = false;
  formRef.value?.resetValidation();
};

const viewOrder = () => {
  router.push({ name: "check-my-coverage", params: { id: importedOrderKey.value } });
};

// Demo mode only: prefill the form and auto-advance into the live PA flow. No-op in production.
useDemoCmmImport({
  submissionKey,
  patientLastName,
  patientDobDate,
  extraContextNotes,
  supplementalDocs,
  importedOrderKey,
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.doc-drop-zone {
  border: 1px solid $lh_dash_border;
  background: transparent;
  transition: border $lh_transition_fast, background $lh_transition_fast;

  &--active {
    border: 2px dashed $lh_color_blue;
    background: rgba($lh_color_blue, 0.04);
  }
}

.advanced {
  border-top: 1px solid $lh_dash_border;

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.75rem 0 0.25rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }

  &__heading {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__chevron {
    color: $lh_color_blue;
    transition: transform $lh_transition_fast;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding-top: 0.5rem;
  }

  &__help {
    margin-left: 4px;
    color: $lh_color_blue_gray;
    cursor: help;
  }
}
</style>
